import type { FC, PropsWithChildren } from "react";
import { useRef } from "react";

import { useSelector } from "@jti/store/reduxHooks";
import { selectFreeShippingThreshold } from "@jti/store/selectors/selectFreeShippingThreshold";

import { useFreeTrial } from "@checkout/hooks/useFreeTrial";
import { useShouldDisplayFreeShippingProgressBar } from "@checkout/hooks/useShouldDisplayFreeShippingProgressBar";

import { FreeShippingProgressBar } from "../FreeShippingProgressBar/FreeShippingProgressBar";

import type { PaymentSummaryBaseProps } from "./PaymentSummaryBase";
import { PaymentSummaryBase } from "./PaymentSummaryBase";

import styles from "./PaymentSummaryBase.module.scss";

type PaymentSummaryProps = Omit<PaymentSummaryBaseProps, "isFreeTrial">;

type ExtendedPaymentSummaryProps = PaymentSummaryProps & {
  showFreeShippingProgressBar?: boolean;
};

export const PaymentSummary: FC<PropsWithChildren<ExtendedPaymentSummaryProps>> = ({
  isPricesLoading,
  prices,
  children,
  showFreeShippingProgressBar = false,
}) => {
  const { isFreeTrial } = useFreeTrial();
  const freeShippingThreshold = useSelector(selectFreeShippingThreshold);
  const subtotal = prices?.subtotal;
  const keepVisibleWhenInitiallyReachedRef = useRef(false);

  if (
    !keepVisibleWhenInitiallyReachedRef.current &&
    subtotal?.value != null &&
    freeShippingThreshold != null &&
    subtotal.value < freeShippingThreshold
  ) {
    keepVisibleWhenInitiallyReachedRef.current = true;
  }

  const { shouldDisplayFreeShippingProgressBar } = useShouldDisplayFreeShippingProgressBar(prices);
  const shouldDisplayBar = showFreeShippingProgressBar && shouldDisplayFreeShippingProgressBar;

  return (
    <>
      {shouldDisplayBar ? (
        <FreeShippingProgressBar
          current={subtotal?.value}
          currency={subtotal?.currency}
          threshold={freeShippingThreshold}
          keepVisibleWhenInitiallyReached={keepVisibleWhenInitiallyReachedRef.current}
          className={styles.freeShippingProgressBar}
        />
      ) : null}
      <PaymentSummaryBase isPricesLoading={isPricesLoading} prices={prices} isFreeTrial={isFreeTrial}>
        {children}
      </PaymentSummaryBase>
    </>
  );
};
